/**
 * Permission types — mirrored from RW-ERP-Service.
 *
 *   Vue:  src/types/draftProducts/attribute-enum.ts
 *         src/api/permissions/permission.api.ts (response shape)
 */

export enum AttributePermission {
  READ = 'read',
  WRITE = 'write',
  HIDE = 'hide',
}

/** Server uses 0/1 for the domain CRUD flags. */
export enum BinaryFlag {
  FALSE = 0,
  TRUE = 1,
}

/**
 * Wire-observed BOOLEAN/NUMERIC forms of a granted flag — native `true` or
 * the numeric `BinaryFlag.TRUE`. Checked by strict-equality membership (see
 * `hasDomainAccess` in `store/PermissionContext.tsx`).
 */
export const GRANTED_FLAG_VALUES: readonly (boolean | BinaryFlag)[] = [true, BinaryFlag.TRUE];

/**
 * Wire-observed STRING forms of a granted flag — `DomainPermissionDTO`
 * fields are a `TryStateEnum` that can serialise as the string `"TRUE"`/`"1"`
 * in addition to native boolean/numeric forms. Compared against the
 * trimmed, lower-cased flag value (see `hasDomainAccess` in
 * `store/PermissionContext.tsx`).
 */
export type GrantedFlagString = 'true' | '1';

export const GRANTED_FLAG_STRINGS: readonly GrantedFlagString[] = ['true', '1'];

export interface RoutePermissionEntry {
  hasPermission: boolean;
}

/**
 * Per-operation permission entry. Operations are server-defined GUIDs
 * (e.g. emulation = '2cdd12f6-13f6-47e4-8a94-87f225b7fcd2').
 */
export interface OperationPermissionEntry {
  hasPermission: boolean;
}

export interface DomainCrudPermissions {
  canCreate: BinaryFlag;
  canRead: BinaryFlag;
  /**
   * Kept, but now OPTIONAL: `DomainPermissionDTO`'s wire field is actually
   * `canUpdate` (below), so this was always `undefined` at runtime despite
   * being declared required — existing readers (`useDomainPermissionMap.ts`,
   * `resolveAttributePermission.ts`) already tolerate that. Left in place,
   * unrenamed, so this stays a type-only addition rather than a breaking
   * change to those files; fixing `canWrite` itself is a separate, wider
   * change out of scope here.
   */
  canWrite?: BinaryFlag;
  /**
   * The update flag as RW-Users-Service actually sends it on the wire —
   * `DomainPermissionDTO`'s field is `canUpdate`, not `canWrite`. Added
   * alongside `canWrite` (rather than replacing it) for the same reason:
   * only CloseHub's domain-CRUD check (`features/closeHub/utils/access.ts`)
   * reads this field.
   */
  canUpdate: BinaryFlag;
  canDelete: BinaryFlag;
  canCreateAssignedToMe?: BinaryFlag;
  canCreateCreatedByMe?: BinaryFlag;
  canReadAssignedToMe?: BinaryFlag;
  canUpdateAssignedToMe?: BinaryFlag;
}

export interface DomainPermissionEntry {
  domainId: string;
  permissions: DomainCrudPermissions;
}

export interface AttributePermissionEntry {
  userId?: string;
  permissions: {
    canRead: BinaryFlag;
    canWrite: BinaryFlag;
    canReadAssignedToMe?: boolean;
    canWriteAssignedToMe?: boolean;
    canReadAssignedToTeam?: boolean;
    canWriteAssignedToTeam?: boolean;
    canReadCreatedByMe?: boolean;
    canWriteCreatedByMe?: boolean;
    canReadCreatedByTeam?: boolean;
    canWriteCreatedByTeam?: boolean;
  };
}

export interface DomainAttributePermissionsEntry {
  domainId: string;
  attributes: Record<string, AttributePermissionEntry>;
}

export interface PermissionResponseData {
  permissions: {
    routePermissions: Record<string, RoutePermissionEntry>;
    operationPermissions?: Record<string, OperationPermissionEntry>;
    domainPermissions: Record<string, DomainPermissionEntry>;
    domainAttributePermissions: Record<string, DomainAttributePermissionsEntry>;
    stageSectionAttributePermissions?: unknown;
  };
}

export interface PermissionResponse {
  data: PermissionResponseData;
}
